import { useState } from 'react';
import { toast } from 'sonner';
import { Edit3, Save, X, ExternalLink, Copy, Sparkles, Send, Archive as ArchiveIcon, XCircle, BookmarkPlus } from 'lucide-react';
import ProfileShell, { DetailField, DetailSection, ProseBlock } from './ProfileShell';
import InviteStatusBadge from './InviteStatusBadge';
import { inviteApi, AUDIENCE_TYPES } from '../../lib/inviteApi';

const EDITABLE = [
  'name', 'organization_name', 'role_title', 'category', 'email',
  'website', 'instagram', 'linkedin', 'x_url', 'city', 'country',
  'short_description', 'why_relevant', 'suggested_angle', 'risk_notes',
  'admin_note',
];

export default function SearchResultProfile({ result, onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(result);
  const [showRaw, setShowRaw] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = editing ? draft : result;

  const update = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const handleSave = async () => {
    if (busy) return;
    setBusy(true);
    const patch = {};
    for (const k of EDITABLE) {
      if ((draft[k] ?? '') !== (result[k] ?? '')) patch[k] = draft[k] ?? null;
    }
    try {
      if (Object.keys(patch).length) {
        await inviteApi.patchSearchResult(result.id, patch);
        toast.success('Saved.');
        onChanged?.();
      }
      setEditing(false);
    } catch (err) {
      toast.error(errorDetail(err, 'Save failed'));
    } finally { setBusy(false); }
  };

  const doConvert = async () => {
    if (busy) return; setBusy(true);
    try {
      await inviteApi.convertSearchResult(result.id);
      toast.success('Converted to invite.');
      onChanged?.();
      onClose?.();
    } catch (err) { toast.error(errorDetail(err, 'Could not convert')); }
    finally { setBusy(false); }
  };
  const doSaveProspect = async () => {
    if (busy) return; setBusy(true);
    try {
      await inviteApi.saveProspect(result.id);
      toast.success('Moved to Prospects.');
      onChanged?.();
      onClose?.();
    } catch (err) { toast.error(errorDetail(err, 'Could not save')); }
    finally { setBusy(false); }
  };
  const doReject = async () => {
    if (busy) return; setBusy(true);
    try { await inviteApi.rejectSearchResult(result.id); toast.success('Rejected.'); onChanged?.(); onClose?.(); }
    catch (err) { toast.error(errorDetail(err, 'Reject failed')); }
    finally { setBusy(false); }
  };
  const doArchive = async () => {
    if (busy) return; setBusy(true);
    try { await inviteApi.archiveSearchResult(result.id); toast.success('Archived.'); onChanged?.(); onClose?.(); }
    catch (err) { toast.error(errorDetail(err, 'Archive failed')); }
    finally { setBusy(false); }
  };

  const dupSuppressed = current.duplicate_status === 'suppressed';
  const dupExisting = current.duplicate_status === 'existing_contact';
  const dupAlreadyInvited = current.duplicate_status === 'already_invited';
  const dupAlreadyTicketed = current.duplicate_status === 'already_ticketed';
  const alreadySaved = current.status === 'saved';
  const alreadyConverted = current.status === 'converted';

  const convertLabel = dupExisting ? 'Link existing contact' : 'Convert to Invite';

  return (
    <ProfileShell onClose={onClose} testid="invite-search-result-profile">
      {/* Header */}
      <div className="px-6 py-5 border-b border-[#1a1a1a] flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#90E0EF]">Search Result</p>
          {editing ? (
            <input value={draft.name || ''} onChange={update('name')} className="invite-input w-full mt-1 text-lg font-outfit text-white !p-1 !border-[#222] bg-transparent" />
          ) : (
            <h2 className="font-outfit text-xl text-white tracking-tight mt-0.5 truncate">{current.name || '—'}</h2>
          )}
          <p className="text-xs font-mono text-[#999] mt-0.5">
            {current.role_title || ''}{current.role_title && current.organization_name ? ' · ' : ''}{current.organization_name || ''}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {current.category && <InviteStatusBadge status={current.category} />}
            {(() => {
              const raw = (current.raw_ai_json && typeof current.raw_ai_json === 'object') ? current.raw_ai_json : {};
              const score = raw.qualification_score;
              if (score == null) return null;
              return <span className="text-[10px] font-mono px-2 py-0.5 border tabular-nums font-semibold uppercase tracking-[0.14em] inline-flex items-center" style={scoreStyle(score)}>score {score}</span>;
            })()}
            {(() => {
              const raw = (current.raw_ai_json && typeof current.raw_ai_json === 'object') ? current.raw_ai_json : {};
              return raw.priority ? <InviteStatusBadge status={raw.priority} /> : null;
            })()}
            {current.email_confidence && <InviteStatusBadge status={current.email_confidence} />}
            {current.duplicate_status && current.duplicate_status !== 'new' && <InviteStatusBadge status={current.duplicate_status} />}
            <InviteStatusBadge status={current.status} strong />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-3 gap-6 px-6 py-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Identifiers */}
          {!editing ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <DetailField label="Email" value={current.email} mono testid="profile-email" />
              <DetailField label="Confidence" value={current.email_confidence} />
              <DetailField label="City" value={current.city} />
              <DetailField label="Country" value={current.country} />
              <DetailField label="Website" value={current.website} link mono />
              <DetailField label="Instagram" value={current.instagram} link mono />
              <DetailField label="LinkedIn" value={current.linkedin} link mono />
              <DetailField label="X / Twitter" value={current.x_url} link mono />
              <DetailField label="Source URL" value={current.source_url} link mono />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Name" value={draft.name} onChange={update('name')} />
              <EditSelect label="Category" value={draft.category} onChange={update('category')} options={AUDIENCE_TYPES} />
              <EditField label="Organization" value={draft.organization_name} onChange={update('organization_name')} />
              <EditField label="Role title" value={draft.role_title} onChange={update('role_title')} />
              <EditField label="Email" value={draft.email} onChange={update('email')} />
              <EditField label="City" value={draft.city} onChange={update('city')} />
              <EditField label="Country" value={draft.country} onChange={update('country')} />
              <EditField label="Website" value={draft.website} onChange={update('website')} />
              <EditField label="Instagram" value={draft.instagram} onChange={update('instagram')} />
              <EditField label="LinkedIn" value={draft.linkedin} onChange={update('linkedin')} />
              <EditField label="X / Twitter" value={draft.x_url} onChange={update('x_url')} />
            </div>
          )}

          {!editing ? (
            <>
              <ProseBlock label="Summary" testid="profile-summary">{current.short_description}</ProseBlock>
              <ProseBlock label="Why relevant" testid="profile-why">{current.why_relevant}</ProseBlock>
              <ProseBlock label="Suggested invite angle" testid="profile-angle">{current.suggested_angle}</ProseBlock>
            </>
          ) : (
            <div className="space-y-3">
              <EditTextarea label="Summary" value={draft.short_description} onChange={update('short_description')} />
              <EditTextarea label="Why relevant" value={draft.why_relevant} onChange={update('why_relevant')} />
              <EditTextarea label="Suggested invite angle" value={draft.suggested_angle} onChange={update('suggested_angle')} />
              <EditTextarea label="Risk / duplicate notes" value={draft.risk_notes} onChange={update('risk_notes')} />
              <EditTextarea label="Admin note" value={draft.admin_note} onChange={update('admin_note')} />
            </div>
          )}

          {/* Qualification */}
          {(() => {
            const raw = (current.raw_ai_json && typeof current.raw_ai_json === 'object') ? current.raw_ai_json : {};
            const score = raw.qualification_score;
            const priority = raw.priority;
            const recAction = raw.recommended_action;
            const tags = Array.isArray(raw.recommended_tags) ? raw.recommended_tags : [];
            const det = raw._deterministic || {};
            const matchReasons = Array.isArray(det.match_reasons) ? det.match_reasons : [];
            const hasContent = score != null || priority || recAction || tags.length || current.risk_notes || current.admin_note || matchReasons.length || current.matched_contact_id || current.matched_ticket_request_id || current.matched_artist_profile_id;
            if (!hasContent) return null;
            return (
              <section className="space-y-2" data-testid="profile-qualification">
                <h4 className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase">Qualification</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {score != null && <DetailField label="Score" value={String(score)} mono />}
                  {priority && <DetailField label="Priority" value={priority} />}
                  {recAction && <DetailField label="Recommended action" value={recAction} />}
                  <DetailField label="Duplicate" value={current.duplicate_status && current.duplicate_status !== 'new' ? current.duplicate_status : null} />
                </div>
                {tags.length > 0 && (
                  <div className="border border-[#161616] bg-[#070707] p-3">
                    <p className="text-[9px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">Tags</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map((t, i) => (
                        <span key={i} className="text-[10px] font-mono text-[#aaa] px-2 py-0.5 border border-[#222] tracking-[0.12em]">{String(t)}</span>
                      ))}
                    </div>
                  </div>
                )}
                <ProseBlock label="Risk notes">{current.risk_notes}</ProseBlock>
                <ProseBlock label="Admin review note">{current.admin_note}</ProseBlock>
                {matchReasons.length > 0 && (
                  <div className="border border-[#161616] bg-[#070707] p-3">
                    <p className="text-[9px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">Why dedupe matched</p>
                    <ul className="text-[11px] font-mono text-[#ccc] leading-relaxed list-disc list-inside space-y-1">
                      {matchReasons.map((m, i) => (<li key={i}>{String(m)}</li>))}
                    </ul>
                  </div>
                )}
              </section>
            );
          })()}

          {/* Matched records */}
          <DetailSection title="Matched records" testid="profile-matched">
            <DetailField label="Matched contact id" value={current.matched_contact_id} mono />
            <DetailField label="Matched ticket request id" value={current.matched_ticket_request_id} mono />
            <DetailField label="Matched artist profile id" value={current.matched_artist_profile_id} mono />
            <DetailField label="Converted contact id" value={current.converted_contact_id} mono />
          </DetailSection>

          {/* Raw AI */}
          {current.raw_ai_json && (
            <div className="border border-[#161616] bg-[#070707]">
              <button
                onClick={() => setShowRaw((v) => !v)}
                data-clickable="true"
                data-testid="profile-toggle-raw"
                className="w-full text-left px-4 py-2.5 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-[#777] hover:text-white"
              >
                Raw AI data <span>{showRaw ? '−' : '+'}</span>
              </button>
              {showRaw && (
                <pre className="px-4 pb-4 text-[10px] font-mono text-[#888] overflow-x-auto max-h-[300px] whitespace-pre-wrap">{JSON.stringify(current.raw_ai_json, null, 2)}</pre>
              )}
            </div>
          )}
        </div>

        {/* Action rail */}
        <aside className="lg:col-span-1 space-y-3">
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4 space-y-2.5">
            <p className="text-[10px] font-mono text-[#777] tracking-[0.22em] uppercase mb-1">Actions</p>

            {!editing && !alreadyConverted && !alreadySaved && !dupSuppressed && !dupAlreadyInvited && !dupAlreadyTicketed && (
              <ActionBtn icon={BookmarkPlus} label="Move to Prospect" onClick={doSaveProspect} tone="violet" testid="profile-save-prospect" />
            )}
            {!editing && !alreadyConverted && !dupSuppressed && (
              <ActionBtn icon={Send} label={convertLabel} onClick={doConvert} tone="cyan" testid="profile-convert" />
            )}
            {!editing && !alreadyConverted && (
              <>
                <ActionBtn icon={XCircle} label="Reject" onClick={doReject} tone="rose" testid="profile-reject" />
                <ActionBtn icon={ArchiveIcon} label="Archive" onClick={doArchive} tone="muted" testid="profile-archive" />
              </>
            )}
            {!editing && (
              <ActionBtn icon={Edit3} label="Edit" onClick={() => { setDraft(result); setEditing(true); }} tone="muted" testid="profile-edit" />
            )}
            {editing && (
              <>
                <ActionBtn icon={Save} label={busy ? 'Saving…' : 'Save edits'} onClick={handleSave} tone="cyan" testid="profile-save" />
                <ActionBtn icon={X} label="Cancel" onClick={() => { setEditing(false); setDraft(result); }} tone="muted" />
              </>
            )}
            <ActionBtn
              icon={Copy}
              label="Copy email"
              onClick={async () => {
                if (!current.email) { toast.error('No email on this prospect.'); return; }
                try { await navigator.clipboard.writeText(current.email); toast.success('Email copied'); }
                catch { toast.message('Copy not allowed in this context'); }
              }}
              tone="muted"
              testid="profile-copy-email"
              disabled={!current.email}
            />
            {current.source_url && (
              <ActionBtn icon={ExternalLink} label="Open source" onClick={() => window.open(current.source_url, '_blank')} tone="muted" testid="profile-open-source" />
            )}
          </div>

          {alreadyConverted && (
            <div className="border p-3 text-[11px] font-mono text-[#7ed4a0] leading-relaxed" style={{ borderColor: 'rgba(126,212,160,0.25)', background: 'rgba(126,212,160,0.05)' }}>
              Already converted to an invite. Open Conversations to manage the thread.
            </div>
          )}
          {alreadySaved && (
            <div className="border p-3 text-[11px] font-mono text-[#c89dff] leading-relaxed" style={{ borderColor: 'rgba(200,157,255,0.25)', background: 'rgba(200,157,255,0.05)' }}>
              In Prospects. Use Convert to Invite when you’re ready to start outreach.
            </div>
          )}
          {dupSuppressed && (
            <div className="border p-3 text-[11px] font-mono text-[#e88ba0] leading-relaxed" style={{ borderColor: 'rgba(232,139,160,0.25)', background: 'rgba(232,139,160,0.05)' }}>
              This address is on the suppression list. Convert and Save are disabled.
            </div>
          )}
        </aside>
      </div>
    </ProfileShell>
  );
}

function EditField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <input className="invite-input w-full" value={value ?? ''} onChange={onChange} />
    </label>
  );
}
function EditSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <select className="invite-select w-full" value={value ?? ''} onChange={onChange}>
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </label>
  );
}
function EditTextarea({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <textarea rows={3} className="invite-input w-full" value={value ?? ''} onChange={onChange} />
    </label>
  );
}

function ActionBtn({ icon: Icon, label, onClick, tone = 'cyan', testid, disabled = false }) {
  const map = {
    cyan: 'border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10',
    violet: 'border-[rgba(200,157,255,0.4)] text-[#c89dff] hover:bg-[rgba(200,157,255,0.07)]',
    rose: 'border-[rgba(232,139,160,0.4)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.06)]',
    muted: 'border-[#333] text-[#aaa] hover:text-white hover:border-[#555]',
  };
  return (
    <button
      onClick={onClick}
      data-clickable="true"
      data-testid={testid}
      disabled={disabled}
      className={`w-full inline-flex items-center gap-2 text-[10px] font-mono px-3 py-2 border uppercase tracking-[0.14em] transition-colors ${
        disabled ? 'opacity-40 cursor-not-allowed border-[#222] text-[#666]' : map[tone]
      }`}
    >
      <Icon className="w-3 h-3"/> {label}
    </button>
  );
}

function errorDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.detail || d.message || fallback;
  return err?.message || fallback;
}

function scoreStyle(score) {
  if (score >= 90) return { color: '#90E0EF', borderColor: 'rgba(144,224,239,0.5)', background: 'rgba(144,224,239,0.1)' };
  if (score >= 75) return { color: '#7ed4a0', borderColor: 'rgba(126,212,160,0.45)', background: 'rgba(126,212,160,0.08)' };
  if (score >= 55) return { color: '#cccccc', borderColor: '#333', background: 'rgba(255,255,255,0.03)' };
  if (score >= 35) return { color: '#e4c07a', borderColor: 'rgba(228,192,122,0.45)', background: 'rgba(228,192,122,0.07)' };
  return { color: '#e88ba0', borderColor: 'rgba(232,139,160,0.45)', background: 'rgba(232,139,160,0.07)' };
}
